import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ShieldCheck, Award, CheckCircle, ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';

const ISO_BADGE_URL = "https://customer-assets.emergentagent.com/job_ima-montaz-preview/artifacts/xjkxagw1_ISO_.png";

export const CertificationSection = () => {
  return (
    <section 
      className="py-24 md:py-32 bg-white"
      data-testid="certification-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Content Side */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 bg-[#001582]/10 rounded-full px-4 py-2 mb-6">
              <span className="w-2 h-2 bg-[#FFC107] rounded-full" />
              <span className="text-[#001582] text-sm font-semibold uppercase tracking-wider">Sertifikasi</span>
            </div>
            
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-[#1a1a1a] mb-6 leading-tight">
              Komitmen terhadap{' '}
              <span className="text-[#001582]">Standar Kualitas</span>
            </h2>
            
            <p className="text-slate-600 text-lg leading-relaxed mb-8">
              PT IMA MONTAZ TEKNINDO berkomitmen menjaga standar kualitas tertinggi melalui 
              sistem manajemen mutu yang terstandarisasi dan tersertifikasi internasional.
            </p>

            {/* ISO Details */}
            <div className="bg-[#f5f7fb] rounded-2xl p-6 mb-8">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 bg-gradient-to-r from-[#001582] to-[#001ea7] rounded-xl flex items-center justify-center flex-shrink-0">
                  <ShieldCheck className="w-7 h-7 text-[#FFC107]" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-xl text-[#1a1a1a] mb-2">
                    ISO 9001:2015
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed mb-4">
                    Quality Management System certification, ensuring consistent quality in all 
                    our products and services.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-center gap-2 text-sm text-slate-700">
                      <CheckCircle className="w-4 h-4 text-[#FFC107]" />
                      Certificate No: IMTQ01-CCCQ03
                    </li>
                    <li className="flex items-center gap-2 text-sm text-slate-700">
                      <CheckCircle className="w-4 h-4 text-[#FFC107]" />
                      Valid until: 1 August 2028
                    </li>
                    <li className="flex items-center gap-2 text-sm text-slate-700">
                      <CheckCircle className="w-4 h-4 text-[#FFC107]" />
                      Accredited by JAS-ANZ & IAF
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <Link to="/sertifikasi">
              <Button 
                size="lg"
                className="bg-gradient-to-r from-[#001582] to-[#001ea7] text-white hover:from-[#001ea7] hover:to-[#001582] font-semibold px-8 py-6 rounded-xl"
                data-testid="certification-cta"
              >
                Lihat Detail Sertifikasi
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>

          {/* Badge Side */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative flex items-center justify-center"
          >
            <div className="relative">
              {/* Background Decoration */}
              <div className="absolute inset-0 bg-gradient-to-br from-[#001582]/10 to-[#FFC107]/10 rounded-full scale-150 blur-3xl" />
              
              {/* ISO Badge */}
              <div className="relative bg-white rounded-3xl shadow-2xl p-10 border border-slate-100" style={{ boxShadow: '0 0 30px rgba(0, 21, 130, 0.15)' }}>
                <img 
                  src={ISO_BADGE_URL}
                  alt="ISO 9001:2015 Certification"
                  className="w-48 md:w-64 h-auto mx-auto"
                />
                <div className="text-center mt-6">
                  <p className="font-heading font-bold text-xl text-[#001582]">Quality Certified</p>
                  <p className="text-slate-500 text-sm mt-1">International Standard</p>
                </div>
              </div>

              {/* Floating Awards */}
              <motion.div
                initial={{ opacity: 0, scale: 0 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="absolute -top-4 -right-4 w-16 h-16 bg-[#FFC107] rounded-2xl flex items-center justify-center shadow-lg"
              >
                <Award className="w-8 h-8 text-[#001582]" />
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.4 }}
                className="absolute -bottom-4 -left-4 w-12 h-12 bg-gradient-to-r from-[#001582] to-[#001ea7] rounded-xl flex items-center justify-center shadow-lg"
              >
                <ShieldCheck className="w-6 h-6 text-[#FFC107]" />
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default CertificationSection;
